import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  User,
  Star,
  Calendar,
  MessageSquare,
  Save,
  CheckCircle2,
  Heart,
  Loader2,
  Phone,
  Mail,
  Building2,
  Briefcase,
  MapPin,
  QrCode,
} from "lucide-react";
import { motion } from "motion/react";
import type { LeadVisitorInterestDto } from "@mvk/shared";
import { leadsService } from "../services/leadsService";
import { SkeletonList } from "./SkeletonList";
import { getScanTargetLabel } from "../services/codeRecognitionService";
import { PageShell } from "./PageShell";
import { PageHeader } from "./PageHeader";
import { GlassCard } from "./GlassCard";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { Button } from "./Button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { Lead, LeadActivity, LeadStatus } from "../types";
import {
  CONTACT_MARK_PRESETS,
  CONTACT_MARK_TONE_CLASS,
  toggleMark,
  markLabel,
} from "../utils/contactMarks";

const STATUS_OPTION_KEYS: Array<{ value: LeadStatus; labelKey: string }> = [
  { value: "new", labelKey: "leads.status_new" },
  { value: "contacted", labelKey: "leads.status_contacted" },
  { value: "in_progress", labelKey: "leads.status_in_progress" },
  { value: "qualified", labelKey: "leads.status_qualified" },
  { value: "closed", labelKey: "leads.status_closed" },
  { value: "rejected", labelKey: "leads.status_rejected" },
];

function formatDateTime(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleString(locale.startsWith("en") ? "en-GB" : "ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export interface LeadDetailViewProps {
  eventId: string;
  leadId: string;
  audience?: "visitor" | "admin";
  /** Back target for PageHeader (exhibitor admin uses /exhibitor/leads). */
  backTo?: string;
}

export function LeadDetailView({
  eventId,
  leadId,
  audience = "admin",
  backTo = "/leads",
}: LeadDetailViewProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [lead, setLead] = useState<Lead | undefined>();
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [visitorInterest, setVisitorInterest] = useState<LeadVisitorInterestDto | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [notes, setNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<LeadStatus>("new");
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detail = await leadsService.getDetail(eventId, leadId, audience);
        if (!cancelled && detail) {
          setLead(detail.lead);
          setNotes(detail.lead.notes ?? "");
          setSelectedStatus(detail.lead.status);
          setTags(detail.lead.tags ?? []);
          setRating(detail.lead.rating ?? 0);
          setActivities(detail.activities);
          setVisitorInterest(detail.visitorInterest);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, leadId, audience]);

  if (loading) {
    return (
      <PageShell>
        <SkeletonList variant="detail" className="pt-2" />
      </PageShell>
    );
  }

  if (!lead) {
    return (
      <PageShell>
        <div className="pt-20 text-center">
          <div className="text-gray-500 mb-4">{t("leads.detail_not_found")}</div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
        </div>
      </PageShell>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await leadsService.update(
        eventId,
        leadId,
        {
          status: selectedStatus,
          notes,
          rating: rating || undefined,
          tags,
        },
        audience,
      );
      if (updated) {
        setLead(updated);
        const detail = await leadsService.getDetail(eventId, leadId, audience);
        if (detail) {
          setActivities(detail.activities);
          setVisitorInterest(detail.visitorInterest);
        }
        toast.success(t("leads.detail_save_ok"));
      }
    } catch {
      toast.error(t("leads.detail_save_fail"));
    } finally {
      setSaving(false);
    }
  };

  const otherCompanies =
    visitorInterest?.scannedCompanies.filter((c) => c.companyId !== lead.companyId) ?? [];
  const ownCompanyScans =
    visitorInterest?.scannedCompanies.find((c) => c.companyId === lead.companyId)?.scanCount ?? 0;

  return (
    <PageShell noPadding>
      <PageHeader
        title={lead.visitorName}
        subtitle={t("leads.detail_lead_ref", { suffix: lead.id.slice(-6) })}
        back
        backTo={backTo}
        right={<LeadStatusBadge status={lead.status} />}
      />

      <div className="px-4 pt-4 pb-24 space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <GlassCard variant="elevated" padding="lg">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                <User size={28} className="text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[20px] text-gray-900 mb-1">{lead.visitorName}</h2>
                <div className="flex items-center gap-2 text-[13px] text-gray-500 mb-2">
                  <span>{getScanTargetLabel(lead.scanType, t)}</span>
                  <span>·</span>
                  <div className="flex items-center gap-1">
                    <Calendar size={12} />
                    <span>{formatDateTime(lead.scannedAt, i18n.language)}</span>
                  </div>
                </div>
                {(lead.visitorCompany || lead.visitorPosition || lead.visitorCity) && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-gray-600 mb-2">
                    {lead.visitorCompany ? (
                      <span className="inline-flex items-center gap-1">
                        <Building2 size={12} className="text-gray-400" />
                        {lead.visitorCompany}
                      </span>
                    ) : null}
                    {lead.visitorPosition ? (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase size={12} className="text-gray-400" />
                        {lead.visitorPosition}
                      </span>
                    ) : null}
                    {lead.visitorCity ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} className="text-gray-400" />
                        {lead.visitorCity}
                      </span>
                    ) : null}
                  </div>
                )}
                {(lead.visitorActivityType || lead.visitorInterestedProducts) && (
                  <div className="mt-1 space-y-1 text-[12px] text-gray-600">
                    {lead.visitorActivityType ? (
                      <p>
                        <span className="text-gray-400">
                          {t("visitor.activity_type")}:{" "}
                        </span>
                        {lead.visitorActivityType}
                      </p>
                    ) : null}
                    {lead.visitorInterestedProducts ? (
                      <p>
                        <span className="text-gray-400">
                          {t("leads.detail_interested")}:{" "}
                        </span>
                        {lead.visitorInterestedProducts}
                      </p>
                    ) : null}
                  </div>
                )}
                {lead.tags && lead.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {lead.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full"
                      >
                        {markLabel(tag, t)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <h3 className="text-[14px] text-gray-900 mb-3">{t("leads.detail_contacts_title")}</h3>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5 text-[13px]">
                <Phone size={14} className="text-indigo-500 flex-shrink-0" />
                {lead.visitorPhone ? (
                  <a href={`tel:${lead.visitorPhone}`} className="text-indigo-600 hover:underline">
                    {lead.visitorPhone}
                  </a>
                ) : (
                  <span className="text-gray-400">{t("leads.detail_phone_missing")}</span>
                )}
              </div>
              <div className="flex items-center gap-2.5 text-[13px]">
                <Mail size={14} className="text-indigo-500 flex-shrink-0" />
                {lead.visitorEmail ? (
                  <a
                    href={`mailto:${lead.visitorEmail}`}
                    className="text-indigo-600 hover:underline truncate"
                  >
                    {lead.visitorEmail}
                  </a>
                ) : (
                  <span className="text-gray-400">{t("leads.detail_email_missing")}</span>
                )}
              </div>
              {!lead.visitorPhone && !lead.visitorEmail ? (
                <p className="text-[12px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  {t("leads.detail_contacts_pending")}
                </p>
              ) : null}
            </div>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <div className="flex items-center gap-2 mb-1">
              <QrCode size={16} className="text-indigo-500" />
              <h3 className="text-[14px] text-gray-900">{t("leads.detail_scanned_at_yours_title")}</h3>
            </div>
            <p className="text-[11px] text-gray-400 mb-3">
              {t("leads.detail_scanned_at_yours_hint")}
              {ownCompanyScans > 0
                ? ` · ${t("leads.detail_scans_total", { n: ownCompanyScans })}`
                : ""}
            </p>
            {(visitorInterest?.scannedAtOurBooth?.length ?? 0) === 0 ? (
              <p className="text-[12px] text-gray-400">
                {t("leads.detail_scanned_at_yours_empty")}
              </p>
            ) : (
              <div className="space-y-1.5">
                {visitorInterest!.scannedAtOurBooth!.map((s, i) => {
                  const typeLabel = getScanTargetLabel(s.scanTargetType, t);
                  return (
                    <div
                      key={`${s.scanTargetId}-${s.scannedAt}-${i}`}
                      className="flex items-center justify-between gap-2 text-[13px] py-1.5 border-b border-gray-50 last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-gray-800">
                          {s.targetName || typeLabel}
                        </div>
                        <div className="text-[11px] text-gray-400">{typeLabel}</div>
                      </div>
                      <span className="text-gray-400 text-[11px] flex-shrink-0">
                        {formatDateTime(s.scannedAt, i18n.language)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <div className="flex items-center gap-2 mb-1">
              <QrCode size={16} className="text-violet-500" />
              <h3 className="text-[14px] text-gray-900">{t("leads.detail_other_scans_title")}</h3>
            </div>
            <p className="text-[11px] text-gray-400 mb-3">
              {t("leads.detail_other_scans_hint")}
              {ownCompanyScans > 1
                ? ` · ${t("leads.detail_own_booth_scans", { count: ownCompanyScans })}`
                : ""}
            </p>
            {otherCompanies.length === 0 ? (
              <p className="text-[12px] text-gray-400">
                {t("leads.detail_other_scans_empty")}
              </p>
            ) : (
              <div className="space-y-2">
                {otherCompanies.map((row, i) => (
                  <div
                    key={row.companyId}
                    className="flex items-center gap-2 text-[13px] py-1.5 border-b border-gray-50 last:border-0"
                  >
                    <span className="text-gray-400 w-4 text-[11px]">{i + 1}</span>
                    <span className="flex-1 truncate text-gray-800">{row.name}</span>
                    <span className="text-indigo-600 flex-shrink-0 text-[12px]">
                      {row.scanCount}×
                    </span>
                    <span className="text-gray-400 text-[11px] flex-shrink-0 whitespace-nowrap">
                      {formatDateTime(row.lastScannedAt, i18n.language)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {(visitorInterest?.scans?.length ?? 0) > 0 ? (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <h4 className="text-[12px] text-gray-500 mb-2">
                  {t("leads.detail_all_scans_title", {
                    count: visitorInterest!.scans.length,
                  })}
                </h4>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {visitorInterest!.scans.map((s, i) => {
                    const typeLabel = getScanTargetLabel(s.scanTargetType, t);
                    const title =
                      s.targetName ||
                      s.companyName ||
                      typeLabel;
                    const sub =
                      s.targetName && s.companyName && s.targetName !== s.companyName
                        ? `${s.companyName} · ${typeLabel}`
                        : typeLabel;
                    return (
                      <div
                        key={`${s.scanTargetId}-${s.scannedAt}-${i}`}
                        className="flex items-start justify-between gap-2 text-[12px] py-1 border-b border-gray-50 last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-gray-800">{title}</div>
                          <div className="truncate text-[11px] text-gray-400">{sub}</div>
                        </div>
                        <span className="text-gray-400 flex-shrink-0 whitespace-nowrap text-[11px]">
                          {formatDateTime(s.scannedAt, i18n.language)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <div className="flex items-center gap-2 mb-1">
              <Heart size={16} className="text-rose-500" />
              <h3 className="text-[14px] text-gray-900">{t("leads.detail_favorites_title")}</h3>
            </div>
            <p className="text-[11px] text-gray-400 mb-3">
              {t("leads.detail_favorites_hint")}
            </p>
            {(visitorInterest?.favoritedProducts?.length ?? 0) === 0 ? (
              <p className="text-[12px] text-gray-400">{t("leads.detail_favorites_empty")}</p>
            ) : (
              <div className="space-y-2">
                {visitorInterest!.favoritedProducts.map((p) => {
                  const isOwn = p.companyId === lead.companyId;
                  return (
                    <div
                      key={p.productId}
                      className={`rounded-xl border px-3 py-2.5 ${
                        isOwn
                          ? "border-rose-200 bg-rose-50/60"
                          : "border-gray-100 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[13px] text-gray-900 truncate">{p.name}</div>
                          <div className="text-[11px] text-gray-500 truncate mt-0.5">
                            {isOwn
                              ? t("leads.detail_own_product")
                              : p.companyName || t("leads.detail_other_exhibitor")}
                            {p.price ? ` · ${p.price}` : ""}
                          </div>
                        </div>
                        {isOwn ? (
                          <span className="flex-shrink-0 text-[10px] text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded-full">
                            {t("leads.detail_own_badge")}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">
                        {t("leads.detail_added_at", {
                          date: formatDateTime(p.addedAt, i18n.language),
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <h3 className="text-[14px] text-gray-900 mb-3">{t("leads.detail_rating_title")}</h3>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all ${
                    value <= rating
                      ? "border-amber-500 bg-amber-50"
                      : "border-gray-200 hover:border-amber-300"
                  }`}
                >
                  <Star
                    size={20}
                    className={`mx-auto ${
                      value <= rating ? "text-amber-500 fill-amber-500" : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <h3 className="text-[14px] text-gray-900 mb-3">{t("leads.col_status")}</h3>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as LeadStatus)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            >
              {STATUS_OPTION_KEYS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <h3 className="text-[14px] text-gray-900 mb-3">{t("annotate.title")}</h3>
            <div className="flex flex-wrap gap-1.5 mb-1">
              {CONTACT_MARK_PRESETS.map((preset) => {
                const on = tags.includes(preset.id);
                const tone = CONTACT_MARK_TONE_CLASS[preset.tone];
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setTags((prev) => toggleMark(prev, preset.id))}
                    className={`px-2.5 py-1 rounded-full border text-[12px] transition-colors ${
                      on ? tone.on : tone.off
                    }`}
                  >
                    {markLabel(preset.id, t)}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-400">{t("leads.detail_marks_export_hint")}</p>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <h3 className="text-[14px] text-gray-900 mb-3 flex items-center gap-2">
              <MessageSquare size={16} className="text-indigo-500" />
              {t("leads.col_notes")}
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("leads.detail_notes_placeholder")}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[14px] resize-none focus:outline-none focus:border-indigo-300"
            />
          </GlassCard>
        </motion.div>

        {activities.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
          >
            <GlassCard variant="default">
              <h3 className="text-[14px] text-gray-900 mb-3">{t("leads.detail_activity_title")}</h3>
              <div className="space-y-3">
                {activities.map((activity, i) => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={14} className="text-indigo-600" />
                      </div>
                      {i < activities.length - 1 && (
                        <div className="w-px h-full bg-gray-200 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-3">
                      <p className="text-[13px] text-gray-700">{activity.description}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {formatDateTime(activity.timestamp, i18n.language)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={saving}
            className="w-full"
          >
            <Save size={18} />
            {saving ? t("visitor.saving") : t("leads.detail_save_button")}
          </Button>
        </motion.div>
      </div>
    </PageShell>
  );
}
